/**
 * ユーザーデータ（EP-1）の共通型とストア境界。
 *
 * 同じ操作面を localStorage（ゲスト）とサーバ `/me/*`（ログイン中）の 2 実装が満たし、
 * 呼び出し側はどちらに書いているかを意識しない。
 */

/** 集合系の種別（履歴は再開位置から導かれる読み取り専用）。 */
export type MarkType = 'bookmark' | 'sticky' | 'shelf';
export type ListType = MarkType | 'history';

/** 集合系の 1 項目。page が無い項目はブック単位（本棚）。 */
export interface UserItem {
  /** ブックの slug。 */
  book: string;
  page?: number;
  /** サーバ一覧のみ付く（一覧表示用）。 */
  title?: string;
  payload?: Record<string, unknown>;
  /** 更新時刻（unix 秒）。マージの新旧判定に使う。 */
  updatedAt: number;
}

/** 再開位置。 */
export interface ProgressEntry {
  book: string;
  page: number;
  updatedAt: number;
}

export interface UserDataStore {
  list(type: ListType): Promise<UserItem[]>;
  put(type: MarkType, item: UserItem): Promise<void>;
  remove(type: MarkType, book: string, page?: number): Promise<void>;
  /** 1 ブックの再開位置（無ければ null）。 */
  progress(book: string): Promise<number | null>;
  saveProgress(book: string, page: number, updatedAt?: number): Promise<void>;
}

/** localStorage に持つスナップショット（ゲストのデータ一式）。 */
export interface LocalSnapshot {
  bookmark: UserItem[];
  sticky: UserItem[];
  shelf: UserItem[];
  progress: Record<string, ProgressEntry>;
  /** サーバへ引き継いだ時刻（unix 秒）。設定済みなら二度とマージしない。 */
  mergedAt?: number;
}

export const MARK_TYPES: readonly MarkType[] = ['bookmark', 'sticky', 'shelf'];

export function emptySnapshot(): LocalSnapshot {
  return { bookmark: [], sticky: [], shelf: [], progress: {} };
}

/** 現在時刻（unix 秒）。 */
export function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

/** 同一項目か（ブック＋ページ。ページ無しはブック単位として一致）。 */
export function sameItem(a: UserItem, book: string, page?: number): boolean {
  return a.book === book && (a.page ?? 0) === (page ?? 0);
}
